import { Task, TaskStatus, ExternalTask, BaseTask, UserProfile, TaskComment } from '../types';
import { TaskService } from '../services/TaskService';

interface TaskManagementProps {
    userProfile: UserProfile | null;
    tasks: (Task | ExternalTask)[];
    externalTasks: ExternalTask[];
    showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
    track: (eventName: string, properties?: any) => void;
    updateExternalTaskStatus: (extTask: ExternalTask, newStatus: string, blockedReason?: string, newAssigneeId?: string, newTitle?: string, newDescription?: string, newPriority?: string) => Promise<void>;
}

export const useTaskManagement = ({ 
    userProfile, 
    tasks, 
    externalTasks, 
    showToast,
    track,
    updateExternalTaskStatus 
}: TaskManagementProps) => {

    const addTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
        if (!userProfile?.orgId) return;
        track('task_created', { 
            teamId: taskData.teamId, 
            priority: taskData.priority,
            hasDependencies: taskData.dependencyIds?.length > 0
        });
        TaskService.addTask({ ...taskData, orgId: userProfile.orgId }, userProfile.id);
    };

    const updateTask = async (taskId: string, data: Partial<Task>) => {
        const task = tasks.find(t => t.id === taskId);
        const taskTitle = task?.title || taskId;
        
        console.log(`[Firestore] Updating task "${taskTitle}" (${taskId}):`, data);
        const extTask = externalTasks.find(t => t.id === taskId);
        
        if (extTask) {
            track('external_task_updated', { 
                taskId, 
                provider: extTask.externalSource,
                fields: Object.keys(data)
            });
            // EXTERNAL REASSIGNMENT: If fields are changing, push to backend
            await updateExternalTaskStatus(
                extTask, 
                data.status || extTask.status, 
                data.blockedReason,
                data.assigneeId !== undefined ? data.assigneeId : undefined,
                data.title !== undefined ? data.title : undefined,
                data.description !== undefined ? data.description : undefined,
                data.priority !== undefined ? data.priority : undefined
            );
            return;
        }

        if (!userProfile) return;
        track('task_updated', { taskId, fields: Object.keys(data) });
        TaskService.updateTask(taskId, data, userProfile.id, taskTitle);
    };

    const updateTaskStatus = async (taskId: string, status: TaskStatus) => {
        const task = tasks.find(t => t.id === taskId);
        const taskTitle = task?.title || taskId;

        console.log(`[Firestore] Changing status of "${taskTitle}" to "${status}"`);
        const extTask = externalTasks.find(t => t.id === taskId);
        
        track('task_status_changed', { 
            taskId, 
            newStatus: status, 
            isExternal: !!extTask,
            provider: extTask?.externalSource 
        });

        if (extTask) {
            await updateExternalTaskStatus(extTask, status);
            return;
        }

        if (!userProfile) return;
        TaskService.updateTaskStatus(taskId, status, userProfile.id, taskTitle);
    };

    const deleteTask = async (taskId: string) => { 
        const task = tasks.find(t => t.id === taskId);
        const taskTitle = task?.title || taskId;
        track('task_deleted', { taskId });
        TaskService.deleteTask(taskId, taskTitle);
    };

    const addTaskComment = async (taskId: string, text: string) => {
        if (!userProfile) return;
        const task = tasks.find(t => t.id === taskId);
        const taskTitle = task?.title || taskId;
        const isExternal = externalTasks.some(t => t.id === taskId);
        
        track('comment_added', { taskId, isExternal });

        const newComment: TaskComment = {
            id: Math.random().toString(36).substring(2, 9),
            userId: userProfile.id,
            userName: userProfile.displayName,
            userPhoto: userProfile.photoURL,
            text: text.trim(),
            createdAt: Date.now()
        };

        TaskService.addComment(taskId, isExternal, newComment, taskTitle);
    };

    const deleteTaskComment = async (taskId: string, commentId: string) => {
        const task = tasks.find(t => t.id === taskId);
        const taskTitle = task?.title || taskId;
        const isExternal = externalTasks.some(t => t.id === taskId);
        TaskService.deleteComment(taskId, isExternal, commentId, taskTitle);
    };

    const getDependencies = (task: BaseTask): (Task | ExternalTask)[] => {
        const suTask = task as Task;
        if (!suTask.dependencyIds || suTask.dependencyIds.length === 0) return [];
        // Note: 'tasks' here is the unified list passed from context
        return tasks.filter(t => suTask.dependencyIds.includes(t.id));
    };

    const isTaskBlocked = (task: BaseTask): boolean => {
        if (task.status === 'blocked') return true;
        const deps = getDependencies(task);
        return deps.some(d => d.status !== 'done');
    };

    return {
        addTask,
        updateTask,
        updateTaskStatus,
        deleteTask,
        addTaskComment,
        deleteTaskComment,
        getDependencies,
        isTaskBlocked
    };
};
